<template>
  <div
    ref="vTailwindPickerRef"
    class="dwp-relative dwp-select-none"
    v-closable="{
      handler: 'onAway',
      exclude: ['currentPicker'],
    }"
    @click="onFeedBack"
    :style="`--bg-tailwind-picker: ${theme.background};`"
  >
    <slot></slot>

    <transition name="dwp-date-picker">
      <div v-show="showPicker || inline">
        <div
          id="dwp-date-picker"
          class="dwp-bg-transparent dwp-mt-3 dwp-z-10"
          :class="[
            { 'inline-mode': inline },
            inline ? 'static' : 'dwp-absolute dwp-bottom-0 dwp-inset-x-0',
            theme.currentColor,
          ]"
        >
          <div
            class="dwp-w-full dwp-h-auto dwp-max-w-xs dwp-transition-all dwp-duration-150 dwp-ease-linear dwp-rounded dwp-overflow-hidden"
            :class="[
              theme.border,
              theme.text,
              inline ? '' : 'dwp-shadow-md',
            ]"
            :style="{ backgroundColor: `var(--bg-tailwind-picker)` }"
          >
            <!-- Navigation of picker-->
            <div class="dwp-relative dwp-p-1">
              <div class="dwp-absolute dwp-inset-0" :class="theme.navigation.background"></div>
              <div class="dwp-flex dwp-justify-between dwp-items-center dwp-relative">
                <div class="dwp-flex-shrink-0 dwp-w-8">
                  <transition name="dwp-date-picker-chevron-left">
                    <div
                      v-if="!enableMonth && !enableYear"
                      class="dwp-rounded-full dwp-overflow-hidden"
                    >
                      <div
                        class="dwp-transition dwp-duration-150 dwp-ease-out dwp-p-2"
                        :class="[
                          visiblePrev
                            ? 'dwp-cursor-pointer'
                            : 'dwp-cursor-not-allowed dwp-opacity-30',
                          theme.navigation.hover,
                        ]"
                        @click="onPrevious()"
                      >
                        <ChevronLeftIcon class="dwp-h-4 dwp-w-auto" />
                      </div>
                    </div>
                  </transition>
                </div>

                <div class="dwp-flex dwp-flex-1">
                  <div
                    class="dwp-flex-1 dwp-rounded dwp-overflow-hidden dwp-py-1 dwp-ml-2 dwp-mr-1 dwp-text-center dwp-cursor-pointer dwp-transition dwp-duration-150 dwp-ease-out"
                    :class="[
                      enableMonth ? theme.navigation.focus : '',
                      theme.navigation.hover,
                    ]"
                    @click="toggleMonth()"
                  >{{ today.format('MMM') }}</div>

                  <div
                    class="dwp-flex-1 dwp-rounded dwp-overflow-hidden dwp-py-1 dwp-mr-2 dwp-ml-1 dwp-text-center dwp-cursor-pointer dwp-transition dwp-duration-150 dwp-ease-out"
                    :class="[
                      enableYear ? theme.navigation.focus : '',
                      theme.navigation.hover,
                    ]"
                    @click="toggleYear()"
                  >{{ today.$y }}</div>
                </div>

                <div class="dwp-flex-shrink-0 dwp-w-8">
                  <transition name="dwp-date-picker-chevron-right">
                    <div
                      v-if="!enableMonth && !enableYear"
                      class="dwp-rounded-full dwp-overflow-hidden"
                    >
                      <div
                        class="dwp-transition dwp-duration-150 dwp-ease-out dwp-p-2"
                        :class="[
                          visibleNext
                            ? 'dwp-cursor-pointer'
                            : 'dwp-cursor-not-allowed dwp-opacity-30',
                          theme.navigation.hover,
                        ]"
                        @click="onNext()"
                      >
                        <ChevronRightIcon class="dwp-h-4 dwp-w-auto" />
                      </div>
                    </div>
                  </transition>
                </div>
              </div>
            </div>

            <!--  Body of picker-->
            <div class="dwp-dwp-smooth-scrolling dwp-overflow-x-hidden dwp-overflow-y-auto">
              <transition name="dwp-date-picker-body">
                <div v-if="!enableMonth && !enableYear" class="dwp-relative">
                  <div class="dwp-flex dwp-flex-no-wrap dwp-py-2" :class="theme.border">
                    <div
                      v-for="day in days"
                      :key="day"
                      class="dwp-w-1/7 dwp-flex dwp-justify-center"
                    >
                      <div
                        class="dwp-leading-relaxed dwp-text-xs"
                        :class="[
                          day === 'Sun'
                            ? theme.picker.holiday
                            : day === 'Fri'
                            ? theme.picker.weekend
                            : '',
                        ]"
                      >{{ day }}</div>
                    </div>
                  </div>

                  <div ref="currentPicker" class="dwp-flex dwp-flex-wrap dwp-relative">
                    <div
                      v-for="(date, i) in previousPicker"
                      :key="`${date.$D}${date.$M}${date.$y}-previous`"
                      class="dwp-w-1/7 dwp-flex dwp-justify-center dwp-my-2px dwp-cursor-not-allowed dwp-text-xs"
                      :class="[
                        i === previousPicker.length - 1 ? 'dwp-rounded-r-full' : '',
                        theme.navigation.background,
                      ]"
                    >
                      <div
                        class="dwp-h-6 dwp-w-6 dwp-flex dwp-justify-center dwp-items-center"
                        :data-tailwind-datepicker="date.$d"
                      >
                        <div
                          class="dwp-text-xs dwp-opacity-75"
                          :class="[
                            date.day() === 0
                              ? theme.picker.holiday
                              : date.day() === 5
                              ? theme.picker.weekend
                              : '',
                          ]"
                        >{{ date.$D }}</div>
                      </div>
                    </div>

                    <div
                      v-for="date in currentPicker"
                      :key="`${date.$D}${date.$M}${date.$y}-current`"
                      class="dwp-w-1/7 dwp-group dwp-flex dwp-justify-center dwp-items-center dwp-my-2px dwp-text-xs"
                    >
                      <div class="dwp-relative dwp-overflow-hidden" :class="theme.picker.rounded">
                        <div
                          v-if="date.$events"
                          class="dwp-absolute dwp-top-0 dwp-right-0 dwp-h-2 dwp-w-2 dwp-z-2"
                          :class="theme.picker.event"
                          :style="{
                            backgroundColor: date.$events.color
                              ? date.$events.color
                              : '',
                          }"
                        ></div>
                        <div
                          class="dwp-relative dwp-h-6 dwp-w-6 dwp-flex dwp-justify-center dwp-items-center dwp-overflow-hidden dwp-text-xs"
                          :class="[
                            theme.picker.rounded,
                            possibleDate(date)
                              ? 'dwp-cursor-pointer'
                              : 'dwp-cursor-not-allowed',
                          ]"
                        >
                          <div
                            v-if="possibleDate(date)"
                            :data-cy="date.$D === today.$D && date.$M === today.$M && possibleDate(date) ? 'current-date' : 'other-date'"
                            class="dwp-absolute dwp-inset-0 dwp-transition dwp-duration-150 dwp-ease-in-out dwp-border dwp-border-dotted dwp-border-transparent"
                            :class="[
                              theme.picker.rounded,
                              possibleDate(date)
                                ? `hover:${theme.picker.selected.border}`
                                : '',
                              date.$D === today.$D && date.$M == today.$M
                                ? `${theme.picker.selected.background} dwp-shadow-xs`
                                : '',
                            ]"
                            @click="changePicker(date)"
                          ></div>
                          <div
                            class="dwp-flex dwp-justify-center dwp-items-center dwp-text-xs"
                            :data-tailwind-datepicker="date.$d"
                          >
                            <div
                              :class="[
                                (holidayDate(date) || date.day() === 0) &&
                                date.$D !== today.$D
                                  ? theme.picker.holiday
                                  : '',
                                date.day() === 5 && date.$D !== today.$D
                                  ? theme.picker.weekend
                                  : '',
                                {
                                  'dwp-z-10 dwp-text-white ':
                                    date.$D === today.$D && date.$M === today.$M  && possibleDate(date),
                                },
                                {
                                  'dwp-opacity-50': !possibleDate(date),
                                },
                              ]"
                            >
                              <span>{{ date.$D }}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div
                      v-for="date in nextPicker"
                      :key="`${date.$D}${date.$M}${date.$y}-next`"
                      class="dwp-w-1/7 dwp-flex dwp-justify-center dwp-my-2px dwp-cursor-not-allowed dwp-text-xs"
                      :class="[
                        date.$D === 1 ? 'dwp-rounded-l-full' : '',
                        theme.navigation.background,
                      ]"
                    >
                      <div
                        class="dwp-h-6 dwp-w-6 dwp-flex dwp-justify-center dwp-items-center"
                        :data-tailwind-datepicker="date.$d"
                      >
                        <div
                          class="dwp-text-xs dwp-opacity-75"
                          :class="[
                            date.day() === (startFromMonday ? 1 : 0)
                              ? theme.picker.holiday
                              : date.day() === (startFromMonday ? 6 : 5)
                              ? theme.picker.weekend
                              : '',
                          ]"
                        >{{ date.$D }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </transition>
              <transition name="dwp-date-picker-months">
                <div
                  v-if="enableMonth"
                  class="dwp-relative dwp-flex dwp-items-center dwp-dwp-smooth-scrolling dwp-overflow-y-auto dwp-overflow-x-hidden"
                >
                  <div class="dwp-flex dwp-flex-wrap dwp-py-1">
                    <div
                      v-for="(month, i) in months"
                      :key="i"
                      class="dwp-w-1/3 dwp-flex dwp-justify-center dwp-items-center dwp-px-2"
                    >
                      <div
                        :class="[
                          i === today.$M
                            ? `${theme.picker.selected.border}`
                            : `${theme.border} ${theme.picker.selected.hover}`,
                        ]"
                        class="dwp-w-full dwp-flex dwp-justify-center dwp-items-center dwp-py-2 dwp-my-1 dwp-transition dwp-duration-150 dwp-ease-out dwp-rounded dwp-border dwp-cursor-pointer"
                        @click="setMonth(i)"
                      >
                        <span class="dwp-font-medium">{{ month }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </transition>
              <transition name="dwp-date-picker-years">
                <div
                  v-if="enableYear"
                  class="dwp-relative dwp-dwp-smooth-scrolling dwp-overflow-y-auto dwp-overflow-x-hidden"
                >
                  <div class="dwp-flex dwp-flex-wrap py-1">
                    <div
                      v-for="(year, i) in years"
                      :key="i"
                      class="dwp-w-1/3 dwp-flex dwp-justify-center dwp-items-center dwp-px-2"
                    >
                      <div
                        :class="[
                          year === today.$y
                            ? `${theme.picker.selected.border}`
                            : `${theme.border} ${theme.picker.selected.hover}`,
                        ]"
                        class="dwp-w-full dwp-flex dwp-justify-center dwp-items-center dwp-py-2 dwp-my-1 dwp-transition dwp-duration-150 dwp-ease-out dwp-rounded dwp-border dwp-cursor-pointer"
                        @click="setYear(year)"
                      >
                        <span class="dwp-font-medium">{{ year }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </transition>
            </div>
            <!-- Event of picker-->
            <div
              v-if="
                currentPicker.filter((o) => o.$events !== undefined).length > 0
              "
              id="dwp-date-picker-footer"
            >
              <transition-group
                name="dwp-date-picker-footer"
                tag="div"
                class="dwp-flex dwp-flex-wrap dwp-border-t dwp-p-1"
                :class="theme.event.border"
              >
                <div
                  v-for="(event, i) in currentPicker.filter(
                    (o) => o.$events !== undefined,
                  )"
                  :key="`${i}-event`"
                  class="dwp-w-full dwp-flex dwp-flex-row dwp-space-x-1 dwp-mb-px"
                >
                  <div class="dwp-inline-flex dwp-justify-end dwp-w-4">
                    <span
                      class="dwp-text-xs dwp-leading-none"
                      :class="theme.picker.holiday"
                    >{{ dayjs(event.$events.date, formatDate).$D }}</span>
                  </div>
                  <div class="dwp-flex dwp-flex-wrap">
                    <div class="dwp-w-full dwp-flex dwp-items-end">
                      <span class="dwp-text-xxs dwp-leading-none">{{ event.$events.description }}</span>
                    </div>
                  </div>
                </div>
              </transition-group>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import dayjs from "dayjs";
import isToday from "dayjs/plugin/isToday";
import customParseFormat from "dayjs/plugin/customParseFormat";
import isBetween from "dayjs/plugin/isBetween";
import localizedFormat from "dayjs/plugin/localizedFormat";
import advancedFormat from "dayjs/plugin/advancedFormat";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";

dayjs.extend(isToday);
dayjs.extend(customParseFormat);
dayjs.extend(isBetween);
dayjs.extend(localizedFormat);
dayjs.extend(advancedFormat);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

let handleOutsideClick;

import { ChevronLeftIcon, ChevronRightIcon } from "vue-feather-icons";

/**
 * Author: kenhyuwa <wahyu.dhiraashandy8@gmail.com
 * Url: https://github.com/kenhyuwa
 **/

export default {
  name: "VueTailwindPicker",

  components: {
    ChevronLeftIcon,
    ChevronRightIcon,
  },

  directives: {
    closable: {
      // https://github.com/TahaSh/vue-closable // resource
      bind(el, binding, vnode) {
        // Here's the click/touchstart handler
        // (it is registered below)
        handleOutsideClick = (e) => {
          e.stopPropagation();
          // Get the handler method name and the exclude array
          // from the object used in v-closable
          const { handler, exclude } = binding.value;

          // This variable indicates if the clicked element is excluded
          let clickedOnExcludedEl = false;
          if (exclude) {
            exclude.forEach((refName) => {
              // We only run this code if we haven't detected
              // any excluded element yet
              if (!clickedOnExcludedEl) {
                // Get the element using the reference name
                const excludedEl = vnode.context.$refs[refName];
                // See if this excluded element
                // is the same element the user just clicked on
                clickedOnExcludedEl = excludedEl
                  ? excludedEl.contains(e.target)
                  : false;
              }
            });
          }

          // We check to see if the clicked element is not
          // the dialog element and not excluded
          if (clickedOnExcludedEl && vnode.context.autoClose) {
            vnode.context[handler]();
          }
          if (!el.contains(e.target) && !clickedOnExcludedEl) {
            // If the clicked element is outside the dialog
            // and not the button, then call the outside-click handler
            // from the same component this directive is used in
            vnode.context[handler]();
          }
        };
        // Register click/touchstart event listeners on the whole page
        document.addEventListener("click", handleOutsideClick);
        document.addEventListener("touchstart", handleOutsideClick);
      },

      unbind() {
        // If the element that has v-closable is removed, then
        // unbind click/touchstart listeners from the whole page
        document.removeEventListener("click", handleOutsideClick);
        document.removeEventListener("touchstart", handleOutsideClick);
      },
    },
  },
  props: {
    init: {
      type: Boolean,
      required: false,
      default: true,
    },
    startDate: {
      type: String,
      required: false,
      default: dayjs().format("YYYY-MM-DD"),
    },
    endDate: {
      type: String,
      required: false,
      default: undefined,
    },
    // Next future
    disableDate: {
      type: Array,
      required: false,
      default: () => [],
    },
    eventDate: {
      type: Array,
      required: false,
      default: () => [],
    },
    formatDate: {
      type: String,
      required: false,
      default: "YYYY-MM-DD",
    },
    // Confused with this
    formatDisplay: {
      type: String,
      required: false,
      default: "YYYY-MM-DD",
    },
    inline: {
      type: Boolean,
      required: false,
      default: false,
    },
    // Not make sure with this
    tailwindPickerValue: {
      type: String,
      required: false,
      default: "",
    },
    // Next future
    dateRange: {
      type: Boolean,
      required: false,
      default: false,
    },
    // Next future
    autoClose: {
      type: Boolean,
      required: false,
      default: true,
    },
    startFromMonday: {
      type: Boolean,
      required: false,
      default: false,
    },
    theme: {
      type: Object,
      required: false,
      default: () => ({
        background: "#FFFFFF",
        text: "dwp-text-gray-700",
        border: "dwp-border-gray-200",
        currentColor: "dwp-text-gray-200",
        navigation: {
          background: "dwp-bg-gray-100",
          hover: "hover:dwp-bg-gray-200",
          focus: "dwp-bg-gray-200",
        },
        picker: {
          rounded: "dwp-rounded-full",
          selected: {
            background: "dwp-bg-primary",
            border: "dwp-border-primary",
            hover: "hover:dwp-border-primary",
          },
          holiday: "dwp-text-red-400",
          weekend: "dwp-text-green-400",
          event: "dwp-bg-indigo-500",
        },
        event: {
          border: "dwp-border-gray-200",
        },
      }),
    },
  },
  data() {
    const startDatepicker = dayjs(this.startDate, this.formatDate);
    // Featured for my own project
    //   .add(
    //   dayjs().hour() >= 20 ? 1 : 0,
    //   'day',
    // )
    const endDatepicker = this.endDate
      ? dayjs(this.endDate, this.formatDate)
      : undefined;
    const today = dayjs(startDatepicker, this.formatDate);
    const months = Array.from(Array(12), (v, i) => {
      return dayjs().month(i).format("MMM");
    });
    const years = Array.from(
      Array(
        this.endDate
          ? endDatepicker.diff(today, "year") + 1
          : today.diff(today, "year") + 36
      ),
      (v, i) => {
        return today.add(i, "year").$y;
      }
    );
    const visibleMonth = false;
    const visibleYear = false;
    const showPicker = false;
    return {
      startDatepicker,
      endDatepicker,
      today,
      visibleMonth,
      months,
      visibleYear,
      years,
      showPicker,
    };
  },
  computed: {
    days() {
      const customWeekend = this.startFromMonday ? 1 : 0;
      return Array.from(Array(7), (v, i) => {
        return dayjs()
          .day(i + customWeekend)
          .format("dd");
      });
    },
    previousPicker() {
      const customWeekend = this.startFromMonday ? 1 : 0;
      const display = [];
      const previous = this.today.date(0);
      const current = this.today.date(0);
      for (let i = 0; i <= current.day() - customWeekend; i++) {
        display.push(previous.subtract(i, "day"));
      }
      return display.sort((a, b) => a.$d - b.$d);
    },
    currentPicker() {
      const customWeekend = this.startFromMonday ? 1 : 0;
      const eventDate = this.eventDate.length > 0 ? this.eventDate : [];
      return Array.from(
        Array(this.today.daysInMonth() - customWeekend),
        (v, i) => {
          const events = this.today.date(++i);
          events.$events = eventDate.find((o) => {
            return o.date === events.format(this.formatDate);
          });
          return events;
        }
      );
    },
    nextPicker() {
      const customWeekend = this.startFromMonday ? 1 : 0;
      const display = [];
      const previous = this.previousPicker.length;
      const current = this.today.daysInMonth();
      for (let i = 1; i <= 42 - (previous + current) + customWeekend; i++) {
        display.push(this.today.date(i).add(1, "month"));
      }
      return display;
    },
    enableMonth() {
      return this.visibleMonth;
    },
    enableYear() {
      return this.visibleYear;
    },
    visiblePrev() {
      if (!this.dateRange) {
        const endOf = this.today.subtract(1, "month").endOf("month");
        const diff = this.startDatepicker.diff(endOf, "day");
        return diff < this.today.daysInMonth() - this.today.$D;
      }
      return true;
    },
    visibleNext() {
      if (!this.dateRange && this.endDate) {
        const startOf = this.today.add(1, "month").startOf("month");
        return this.endDatepicker.diff(startOf, "day") > 0;
      }
      return true;
    },
  },
  watch: {
    showPicker(prev, next) {
      if (prev) {
        this.visibleMonth = next;
        this.visibleYear = next;
      }
    },
  },

  mounted() {
    if (this.init) this.emit();
  },

  methods: {
    dayjs,
    emit() {
      //   this.$emit("change", this.today.format(this.formatDate));
      // this.$emit("change", this.today.toDate());
    },
    changePicker(date) {
      this.today = date;
      this.$emit("change", this.today.toDate());
      this.$emit("close");
      this.showPicker = !this.showPicker;
    },
    onPrevious() {
      if (this.visiblePrev) {
        const today = this.today
          .set("month", this.today.$M === 0 ? 11 : this.today.$M - 1)
          .set("year", this.today.$M === 0 ? this.today.$y - 1 : this.today.$y);
        if (this.possibleDate(today)) {
          this.today = today;
        } else {
          this.today = this.startDatepicker;
        }
        // this.emit();
      }
    },
    onNext() {
      if (this.visibleNext) {
        const today = this.today
          .set("month", (this.today.$M + 1) % 12)
          .set(
            "year",
            this.today.$M === 11 ? this.today.$y + 1 : this.today.$y
          );
        if (this.possibleDate(today)) {
          this.today = today;
        } else {
          this.today = this.endDatepicker;
        }
        // this.emit();
      }
    },
    possibleStartDate(date) {
      return this.dateRange
        ? true
        : date.isSameOrAfter(this.startDatepicker, "day");
    },
    possibleEndDate(date) {
      if (this.endDate) {
        return this.dateRange
          ? true
          : date.isSameOrBefore(this.endDatepicker, "day");
      }
      return false;
    },
    possibleDate(date) {
      if (this.endDate) {
        return this.possibleStartDate(date) && this.possibleEndDate(date);
      }
      return this.possibleStartDate(date);
    },
    holidayDate(date) {
      return !!(date.$events && date.$events.holiday);
    },
    toggleMonth() {
      this.visibleMonth = !this.visibleMonth;
            console.log("toggle", this.visibleMonth);
      if (this.visibleMonth) {
        this.visibleYear = false;
      }
    },
    toggleYear() {
      this.visibleYear = !this.visibleYear;
      if (this.visibleYear) {
        this.visibleMonth = false;
      }
    },
    setMonth(month) {
      if (this.possibleDate(this.today.set("month", month))) {
        this.today = this.today.set("month", month);
      } else {
        this.today = this.startDatepicker;
      }
      this.emit();
      this.toggleMonth();
    },
    setYear(year) {
      if (this.possibleDate(this.today.set("year", year))) {
        this.today = this.today.set("year", year);
      } else {
        this.today = this.startDatepicker;
      }
      this.emit();
      this.toggleYear();
    },
    onAway() {
      this.showPicker = false;
    },
    onFeedBack() {
      this.showPicker = true;
    },
  },
};
</script>
